import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import DatePicker from "react-datepicker";
import StaffLayout from "./staff/StaffLayout";
import "react-datepicker/dist/react-datepicker.css";
import "./StaffEditBooking.css";
import { isMassageSessionType } from "../utils/sessionTypeUtils";
import { resolveGuestBadgeVariant } from "../utils/addonIndicatorUtils";

// STAFF-EDIT-BOOKING-GUEST-STATE hotfix (R-029, 2026-06-21):
// Edit Booking "Add Guest" checkbox previously read only `b.addGuest` on
// initial load, so corp-tier $15 double-occupancy bookings (which set
// `doubleOccupancy: true` and leave `addGuest: false` per ADR-042 D1)
// rendered as UNCHECKED even when Session Details modal correctly showed
// "Add Guest: Yes (Corp)". Staff Save would then silently strip the
// doubleOccupancy flag — but the add-on PaymentIntent still existed in
// Stripe, leaving the customer charged for an add-on no longer on the
// booking record.
//
// This hotfix:
//   1. Resolves checkbox state from BOTH `addGuest` and `doubleOccupancy`
//      via the shared `resolveGuestBadgeVariant` helper.
//   2. Tracks which source field was set (`guestSourceField`) so toggle-off
//      clears the right field.
//   3. Disables uncheck when `addOnPaymentStatus === "paid"` to prevent the
//      silent-strip hazard until the full add-on removal lifecycle ships
//      (ADD-ON-REMOVAL-LIFECYCLE-001).
//
// CFG=YES — money-path adjacent (add-on charge). Edit page can no longer
// silently strip a paid add-on field.

const warnDoubleOccupancyAndGuest = (booking) => {
  // eslint-disable-next-line no-console
  console.warn(
    "[STAFF-EDIT-BOOKING-GUEST-STATE] Booking has both addGuest and doubleOccupancy — ADR-042 D1 invariant violation",
    { bookingId: booking && booking._id }
  );
};

const StaffEditBooking = () => {
  const { id } = useParams(); // Booking ID from URL
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem("jwtToken");
  const returnTo = location.state?.returnTo || "/staff";

  // Local state for booking fields
  const [booking, setBooking] = useState(null);
  const [sessionType, setSessionType] = useState("");
  const [appointmentDate, setAppointmentDate] = useState(null);
  const [suiteAssignment, setSuiteAssignment] = useState("");
  const [addGuest, setAddGuest] = useState(false);
  // Tracks which BE field originally provided the "guest" signal so the
  // toggle-off handler clears the right field (addGuest vs doubleOccupancy).
  // null when neither was set at load.
  const [guestSourceField, setGuestSourceField] = useState(null);
  const [aromatherapy, setAromatherapy] = useState(false);
  const [halotherapy, setHalotherapy] = useState(false);
  const [sendCancellationEmail, setSendCancellationEmail] = useState(true);
  const [availableSuites, setAvailableSuites] = useState([]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const isMassageBooking = isMassageSessionType(sessionType);
  const isLockedPaidMassageBooking =
    booking?.sessionSourceType === "paid-service" &&
    isMassageSessionType(booking?.sessionType);

  // Fetch booking details
  useEffect(() => {
    const fetchBooking = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/staff/bookings/${id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (response.data.booking) {
          const b = response.data.booking;
          setBooking(b);
          setSessionType(b.sessionType);
          setAppointmentDate(new Date(b.appointmentDate));
          // STAFF-EDIT-BOOKING-GUEST-STATE: resolve checkbox state from
          // BOTH addGuest and doubleOccupancy using the shared helper.
          // Variants: 'guest' (addGuest), 'guestCorp' (doubleOccupancy),
          // 'both' (defensive ADR-042 D1 violation), 'none' (neither).
          const guestVariant = resolveGuestBadgeVariant(b);
          if (guestVariant === "both") {
            warnDoubleOccupancyAndGuest(b);
            setAddGuest(true);
            // Defensive precedence: addGuest source-of-truth so toggle-off
            // clears the simpler $25 field; the invariant violation is
            // logged for staff visibility.
            setGuestSourceField("addGuest");
          } else if (guestVariant === "guestCorp") {
            setAddGuest(true);
            setGuestSourceField("doubleOccupancy");
          } else if (guestVariant === "guest") {
            setAddGuest(true);
            setGuestSourceField("addGuest");
          } else {
            setAddGuest(false);
            setGuestSourceField(null);
          }
          setAromatherapy(b.aromatherapy);
          setHalotherapy(b.halotherapy);
          // Set the initial suite assignment as a string
          setSuiteAssignment(
            b.suiteAssignment ? b.suiteAssignment.number.toString() : ""
          );
        } else {
          setError("No booking found.");
        }
      } catch (err) {
        console.error(
          "Error fetching booking:",
          err.response ? err.response.data : err
        );
        setError(
          err.response?.data?.message || "Error fetching booking details."
        );
      }
    };
    fetchBooking();
  }, [id, token]);

  // Update suiteAssignment when booking changes
  useEffect(() => {
    if (booking && booking.suiteAssignment) {
      setSuiteAssignment(booking.suiteAssignment.number.toString());
    }
  }, [booking]);

  // Fetch available suites when sessionType or appointmentDate changes
  useEffect(() => {
    const fetchSuites = async () => {
      if (!appointmentDate || !sessionType) return;
      try {
        const dateObj = new Date(appointmentDate);
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, "0");
        const day = String(dateObj.getDate()).padStart(2, "0");
        const dateString = `${year}-${month}-${day}`;
        const endpoint =
          sessionType === "infrared"
            ? `${process.env.REACT_APP_API_BASE_URL}/suites/sauna`
            : isMassageSessionType(sessionType)
            ? null
            : `${process.env.REACT_APP_API_BASE_URL}/suites/redlight`;

        if (!endpoint) {
          const massageSuiteNumber =
            booking?.suiteAssignment?.number || 1;
          setAvailableSuites([
            {
              id: massageSuiteNumber,
              suiteNumber: massageSuiteNumber,
              name: `Massage Suite #${massageSuiteNumber}`,
            },
          ]);
          return;
        }

        const response = await axios.get(endpoint, {
          headers: { Authorization: `Bearer ${token}` },
          params: { date: dateString, time: appointmentDate.toISOString() },
        });
        let suites = response.data.suites || [];
        // Ensure the current booking's suite appears in the list
        if (booking && booking.suiteAssignment) {
          const currentSuiteNumber = booking.suiteAssignment.number;
          const found = suites.find(
            (s) => s.suiteNumber === currentSuiteNumber
          );
          if (!found) {
            suites.push({
              id: currentSuiteNumber,
              suiteNumber: currentSuiteNumber,
              name:
                booking.suiteAssignment.name || `Suite ${currentSuiteNumber}`,
            });
          }
        }
        setAvailableSuites(suites);
      } catch (err) {
        console.error(
          "Error fetching available suites:",
          err.response ? err.response.data : err
        );
        setAvailableSuites([]);
      }
    };
    fetchSuites();
  }, [sessionType, appointmentDate, token, booking]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");

    // Validate appointment date
    if (!appointmentDate || isNaN(appointmentDate.getTime())) {
      setError("Please select a valid appointment date and time.");
      return;
    }

    // STAFF-EDIT-BOOKING-GUEST-STATE: resolve which BE fields to send for
    // the guest add-on based on the current checkbox state + which source
    // field was set at load. Send BOTH fields explicitly so the BE $set
    // correctly clears the right side (the previous code sent only
    // `addGuest`, which silently left `doubleOccupancy: true` in the DB
    // even when staff unchecked the guest box).
    let resolvedAddGuest;
    let resolvedDoubleOccupancy;
    if (isMassageBooking) {
      resolvedAddGuest = false;
      resolvedDoubleOccupancy = false;
    } else if (!addGuest) {
      // Checkbox unchecked at submit → clear whichever source field was set.
      resolvedAddGuest = false;
      resolvedDoubleOccupancy = false;
    } else if (guestSourceField === "doubleOccupancy") {
      // Preserve corp-tier source — staff cannot add NEW corp-double-occ
      // via Edit page (creation is wizard-only); we only preserve existing.
      resolvedAddGuest = false;
      resolvedDoubleOccupancy = true;
    } else {
      // 'addGuest' source OR newly-checked (no source) → default to the
      // simpler non-corp $25 path.
      resolvedAddGuest = true;
      resolvedDoubleOccupancy = false;
    }

    // Prepare updated fields.
    const updates = {
      sessionType,
      appointmentDate: appointmentDate.toISOString(),
      suiteAssignment: {
        type: isMassageBooking
          ? "massage"
          : sessionType === "infrared"
          ? "sauna"
          : "redlight",
        number: suiteAssignment !== "" ? Number(suiteAssignment) : null,
      },
      addGuest: resolvedAddGuest,
      doubleOccupancy: resolvedDoubleOccupancy,
      aromatherapy: isMassageBooking ? false : aromatherapy,
      halotherapy: isMassageBooking ? false : halotherapy,
    };

    // If suiteAssignment is null (or falsy), preserve the current assignment.
    if (updates.suiteAssignment.number === null) {
      updates.suiteAssignment = booking.suiteAssignment;
    }

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/staff/bookings/${id}`,
        updates,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setMessage(response.data.message || "Booking updated successfully!");
      setTimeout(() => navigate(returnTo), 1500);
    } catch (err) {
      console.error(
        "Error updating booking:",
        err.response ? err.response.data : err
      );
      setError(err.response?.data?.message || "Error updating booking.");
    }
  };

  const handleCancel = () => {
    navigate(returnTo);
  };

  const handleCancelBooking = async () => {
    // Confirm cancellation
    if (!window.confirm("Are you sure you want to cancel this booking?")) {
      return;
    }
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/staff/bookings/${id}`,
        {
          data: { sendCustomerEmail: sendCancellationEmail },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setMessage(response.data.message || "Booking cancelled successfully!");
      setTimeout(() => navigate(returnTo), 1500);
    } catch (err) {
      console.error(
        "Error cancelling booking:",
        err.response ? err.response.data : err
      );
      setError(err.response?.data?.message || "Error cancelling booking.");
    }
  };

  if (error) {
    return <p className="error-message">{error}</p>;
  }
  if (!booking) {
    return <p>Loading booking details...</p>;
  }

  return (
    <StaffLayout>
      <div className="staff-edit-booking">
        <h1>Edit Booking</h1>
        {message && <p className="success-message">{message}</p>}
        {error && <p className="error-message">{error}</p>}
        {!booking ? (
          <p>Loading booking details...</p>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="sessionType">Session Type:</label>
              <select
                id="sessionType"
                value={sessionType}
                onChange={(e) => setSessionType(e.target.value)}
                disabled={isLockedPaidMassageBooking}
              >
                <option value="infrared">Infrared Sauna Session</option>
                <option value="redlight">Redlight Bed Session</option>
                <option value="massage_60">60 Minute Massage</option>
                <option value="massage_90">90 Minute Massage</option>
              </select>
              {isLockedPaidMassageBooking && (
                <p className="form-help-text">
                  Paid massage bookings can be rescheduled, but changing between
                  60 and 90 minutes requires cancelling and rebooking so the
                  payment stays accurate.
                </p>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="appointmentDate">Appointment Date & Time:</label>
              <DatePicker
                id="appointmentDate"
                selected={appointmentDate}
                onChange={(date) => setAppointmentDate(date)}
                showTimeSelect
                timeFormat="h:mm aa"
                timeIntervals={15}
                dateFormat="MMMM d, yyyy h:mm aa"
                minTime={new Date().setHours(9, 0)} // 9:00 AM
                maxTime={new Date().setHours(20, 0)} // 8:00 PM
                timeCaption="Time"
                showTimeSelectOnly={false}
                injectTimes={[]}
                filterTime={(time) => {
                  // Only allow times between 9 AM and 8 PM
                  const hours = new Date(time).getHours();
                  return hours >= 9 && hours < 20;
                }}
              />
            </div>
            <div className="form-group">
              <label htmlFor="suiteSelection">
                Select{" "}
                {isMassageBooking
                  ? "Massage Suite"
                  : sessionType === "infrared"
                  ? "Sauna Suite"
                  : "Red Light Bed Suite"}
                :
              </label>
              <select
                id="suiteSelection"
                value={suiteAssignment}
                onChange={(e) => setSuiteAssignment(e.target.value)}
                required
              >
                <option value="">-- Select a Suite --</option>
                {availableSuites.map((suite) => (
                  <option key={suite.id} value={suite.suiteNumber.toString()}>
                    {isMassageBooking
                      ? suite.name
                      : `${suite.name} (Suite ${suite.suiteNumber})`}
                  </option>
                ))}
              </select>
            </div>
            {sessionType === "infrared" && (
              <>
                <div className="form-group checkbox-group">
                  {(() => {
                    // STAFF-EDIT-BOOKING-GUEST-STATE: disable uncheck when
                    // the add-on has already been charged. Silent-strip via
                    // $set would leave the customer charged for an add-on
                    // no longer on the booking. Full removal lifecycle is
                    // ADD-ON-REMOVAL-LIFECYCLE-001.
                    const guestAddOnPaid =
                      booking?.addOnPaymentStatus === "paid" &&
                      guestSourceField !== null;
                    const disableUncheck = guestAddOnPaid && addGuest;
                    const tooltipText = disableUncheck
                      ? "This guest add-on is already paid. Use Cancel Booking workflow to remove and resolve payment. Full add-on removal workflow coming soon (ADD-ON-REMOVAL-LIFECYCLE-001)."
                      : undefined;
                    return (
                      <>
                        <label title={tooltipText}>
                          <input
                            type="checkbox"
                            name="addGuest"
                            checked={addGuest}
                            disabled={disableUncheck}
                            onChange={(e) => {
                              const next = e.target.checked;
                              setAddGuest(next);
                              if (!next) {
                                // Toggle-off clears the source so submit
                                // sends both BE fields as false.
                                setGuestSourceField(null);
                              } else if (guestSourceField === null) {
                                // Newly-checked from empty → addGuest path
                                // (Edit page cannot create corp-double-occ).
                                setGuestSourceField("addGuest");
                              }
                            }}
                          />{" "}
                          Add Guest
                        </label>
                        {disableUncheck && (
                          <p
                            className="form-help-text"
                            data-testid="guest-paid-disabled-hint"
                          >
                            {tooltipText}
                          </p>
                        )}
                      </>
                    );
                  })()}
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="aromatherapy"
                      checked={aromatherapy}
                      onChange={(e) => setAromatherapy(e.target.checked)}
                    />{" "}
                    Aromatherapy
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="halotherapy"
                      checked={halotherapy}
                      onChange={(e) => setHalotherapy(e.target.checked)}
                    />{" "}
                    Halotherapy
                  </label>
                </div>
              </>
            )}
            <div className="form-actions">
              <label className="form-helper checkbox-inline">
                <input
                  type="checkbox"
                  checked={sendCancellationEmail}
                  onChange={(e) => setSendCancellationEmail(e.target.checked)}
                />{" "}
                Send cancellation email to customer
              </label>
              <div className="form-actions-buttons">
                <button type="submit" className="action-btn action-btn-primary">
                  Save Changes
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="action-btn action-btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCancelBooking}
                  className="action-btn action-btn-danger"
                >
                  Cancel Booking
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </StaffLayout>
  );
};

export default StaffEditBooking;
